import {useCallback, useLayoutEffect, useRef, useState} from 'react';

import {twx} from '@/utils/tailwindUtils';

import {
    EndColumnFooter,
    EndColumnScrollSection,
    EndColumnShell,
    useEndColumnScrollFooterVisibility,
} from '../components/AutoDeliveryDcxPageDesktopLayout';
import {
    RescheduleDesktopCalendarSlide,
    RescheduleDesktopFrequencySlide,
    RescheduleInlineFooter,
} from './ReschedulePopup/components';
import {useRescheduleFlow} from './ReschedulePopup/RescheduleFlowContext';
import {RESCHEDULE_STEPS, type RescheduleStep} from './ReschedulePopup/reschedulePopupSteps';

const getSlideIndex = (isOpen: boolean, step: RescheduleStep, hasFrequencyStep: boolean): number => {
    if (!isOpen || step === RESCHEDULE_STEPS.SUCCESS || step === RESCHEDULE_STEPS.ERROR) {
        return 0;
    }

    if (step === RESCHEDULE_STEPS.FREQUENCY) {
        return hasFrequencyStep ? 1 : 0;
    }

    if (step === RESCHEDULE_STEPS.CALENDAR || step === RESCHEDULE_STEPS.CONFIRMATION) {
        return hasFrequencyStep ? 2 : 1;
    }

    return 0;
};

interface EndSlidePanelProps {
    panelIndex: number;
    activeIndex: number;
    panelCount: number;
    viewportRef: React.RefObject<HTMLDivElement | null>;
    divideSections: boolean;
    children: React.ReactNode;
    footer?: React.ReactNode;
}

const EndSlidePanel = ({
    panelIndex,
    activeIndex,
    panelCount,
    viewportRef,
    divideSections,
    children,
    footer,
}: EndSlidePanelProps) => {
    const isActive = panelIndex === activeIndex;
    const isScrollVisible = useEndColumnScrollFooterVisibility(viewportRef, isActive);

    const panelRef = useCallback(
        (node: HTMLDivElement | null) => {
            if (node) {
                if (isActive) {
                    node.removeAttribute('inert');
                } else {
                    node.setAttribute('inert', '');
                }
            }
        },
        [isActive]
    );

    return (
        <div
            ref={panelRef}
            className='flex h-full min-h-0 flex-col'
            style={{flex: `0 0 ${100 / panelCount}%`}}
            aria-hidden={!isActive}
        >
            <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
                <EndColumnScrollSection viewportRef={viewportRef} divideSections={divideSections}>
                    {children}
                </EndColumnScrollSection>
            </div>
            {footer && <EndColumnFooter isScrollVisible={isScrollVisible}>{footer}</EndColumnFooter>}
        </div>
    );
};

interface RescheduleDesktopEndColumnProps {
    defaultEndBody: React.ReactNode;
    defaultFooter: React.ReactNode | undefined;
}

const RescheduleDesktopEndColumn = ({defaultEndBody, defaultFooter}: RescheduleDesktopEndColumnProps) => {
    const {isOpen, step, hasFrequencyStep} = useRescheduleFlow();
    const panelCount = hasFrequencyStep ? 3 : 2;
    const activeIndex = getSlideIndex(isOpen, step, hasFrequencyStep);

    const [transitionEnabled, setTransitionEnabled] = useState(false);

    useLayoutEffect(() => {
        let outerRafId = 0;
        let innerRafId = 0;
        let isCancelled = false;

        outerRafId = requestAnimationFrame(() => {
            innerRafId = requestAnimationFrame(() => {
                if (!isCancelled) {
                    setTransitionEnabled(true);
                }
            });
        });

        return () => {
            isCancelled = true;
            cancelAnimationFrame(outerRafId);
            cancelAnimationFrame(innerRafId);
        };
    }, []);

    const viewportRef0 = useRef<HTMLDivElement>(null);
    const viewportRef1 = useRef<HTMLDivElement>(null);
    const viewportRef2 = useRef<HTMLDivElement>(null);

    return (
        <EndColumnShell>
            <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
                <div
                    className={twx(
                        'flex h-full min-h-0 flex-row',
                        transitionEnabled
                            ? 'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out motion-reduce:transition-none'
                            : 'motion-safe:duration-0 motion-reduce:transition-none'
                    )}
                    style={{
                        width: `${panelCount * 100}%`,
                        transform: `translateX(-${(activeIndex * 100) / panelCount}%)`,
                    }}
                >
                    <EndSlidePanel
                        panelIndex={0}
                        activeIndex={activeIndex}
                        panelCount={panelCount}
                        viewportRef={viewportRef0}
                        divideSections
                        footer={defaultFooter}
                    >
                        {defaultEndBody}
                    </EndSlidePanel>

                    {hasFrequencyStep && (
                        <EndSlidePanel
                            panelIndex={1}
                            activeIndex={activeIndex}
                            panelCount={panelCount}
                            viewportRef={viewportRef1}
                            divideSections={false}
                        >
                            <RescheduleDesktopFrequencySlide />
                        </EndSlidePanel>
                    )}

                    <EndSlidePanel
                        panelIndex={hasFrequencyStep ? 2 : 1}
                        activeIndex={activeIndex}
                        panelCount={panelCount}
                        viewportRef={hasFrequencyStep ? viewportRef2 : viewportRef1}
                        divideSections={false}
                        footer={<RescheduleInlineFooter />}
                    >
                        <RescheduleDesktopCalendarSlide />
                    </EndSlidePanel>
                </div>
            </div>
        </EndColumnShell>
    );
};

export default RescheduleDesktopEndColumn;
